import { useMutation } from '@tanstack/react-query';
import { useEffect } from 'react';
import { noteOperations, type NoteOperation } from './studyNotebookOperationsApi';
import { useStableKey } from './useStableKey';
import { analysisErrorMessage } from './studyAnalysisErrors';
import { getKidSub } from '@/auth/authStore';
import { useAudioScriptDraft } from './useAudioScriptDraft';

export function StudyAudioScriptApproval({
  noteId,
  operation,
  disabled,
  onChanged,
  onUnsavedChange,
}: {
  noteId: string;
  operation: NoteOperation;
  disabled: boolean;
  onChanged: () => void;
  onUnsavedChange?: (dirty: boolean) => void;
}) {
  const draft = useAudioScriptDraft(noteId, operation);
  const script = draft.script;
  useEffect(() => {
    onUnsavedChange?.(draft.dirty);
    return () => onUnsavedChange?.(false);
  }, [draft.dirty, onUnsavedChange]);
  const key = useStableKey(`${getKidSub()}:audio-approval:${operation.id}:${operation.revision}`);
  const approval = useMutation({
    mutationFn: async () => {
      await draft.flush();
      return noteOperations.change(noteId, operation.id, 'audio-approval', {
        expected_revision: draft.revision(),
        script: script.trim(),
        request_id: key.get(),
      });
    },
    onSuccess: () => {
      key.clear();
      onChanged();
    },
  });
  return (
    <section className="mt-4 space-y-3" aria-label="Review audio script">
      <label className="block text-sm font-bold">
        Read and edit your audio script
        <textarea
          className="mt-2 min-h-56 w-full rounded-xl border border-hairline bg-canvas-pure p-3 text-base leading-relaxed"
          value={script}
          maxLength={6000}
          disabled={disabled || approval.isPending}
          onChange={(event) => {
            key.clear();
            draft.change(event.target.value);
          }}
        />
      </label>
      <p role="status" className="text-sm text-slate2">
        {draft.status}
      </p>
      <button
        className="btn-pill-secondary"
        type="button"
        disabled={disabled || approval.isPending}
        onClick={() => void draft.reload().catch(() => undefined)}
      >
        Reopen saved script
      </button>
      <button
        className="btn-pill-secondary"
        type="button"
        disabled={disabled || approval.isPending}
        onClick={() => void draft.flush().catch(() => undefined)}
      >
        Save script draft
      </button>
      <p className="text-sm text-slate2">
        Audio has not been made yet. The {operation.stars_charged}★ already confirmed covers this
        script and one audio version. Check the words before continuing; cancel to return your
        Stars.
      </p>
      <button
        type="button"
        className="btn-pill-primary"
        disabled={disabled || approval.isPending || script.trim().length < 20}
        onClick={() => approval.mutate()}
      >
        {approval.isPending ? 'Confirming…' : 'Confirm script & make audio'}
      </button>
      {approval.error && (
        <p role="alert" className="text-sm text-danger-600">
          {analysisErrorMessage(
            approval.error,
            'The script was not confirmed. Your edits are still here; retry or reopen the saved item.',
          )}
        </p>
      )}
    </section>
  );
}
